/**
 * Fiber → DOM resolution utilities for the debug panel overlay system.
 *
 * React function component fibers have stateNode === null.
 * To find the actual DOM element we walk the child fiber tree until we hit a
 * HostComponent fiber (tag === 5) whose stateNode is an HTMLElement.
 *
 * WARNING: Uses React internal fiber APIs. Dev-only — never import this in
 * production code paths (always guard with NODE_ENV === "development").
 */

/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- entire file uses React internal fiber APIs which are untyped by design */

import type { Fiber, RenderedComponentWithFibers } from "./fiber-dom.types";

export type { Fiber, RenderedComponentWithFibers } from "./fiber-dom.types";

/**
 * Walk a fiber subtree (depth-first) to find the first rendered DOM element.
 * Returns null when the fiber renders nothing (portals, pure context, etc.) or
 * when the stateNode has already been detached after a React reconcile.
 */
export function fiberToDOM(fiber: Fiber): HTMLElement | null {
  if (!fiber) return null;

  // HostComponent fibers (tag 5) have their DOM node as stateNode.
  // Assign to `unknown` first so instanceof narrows without further unsafe access.
  if (fiber.tag === 5) {
    const node: unknown = fiber.stateNode;
    if (node instanceof HTMLElement) return node;
  }

  // Walk child tree depth-first: try child first, then iterate siblings.
  let child: Fiber = fiber.child;
  while (child) {
    if (child.tag === 5) {
      const node: unknown = child.stateNode;
      if (node instanceof HTMLElement) return node;
    }
    const deeper = fiberToDOM(child);
    if (deeper) return deeper;
    child = child.sibling;
  }

  return null;
}

/**
 * Resolve all rendered DOM rects for a component (may have multiple instances).
 * Stale/detached fibers are silently skipped.
 */
export function getComponentRects(
  component: RenderedComponentWithFibers,
): DOMRect[] {
  const rects: DOMRect[] = [];
  for (const fiber of component.fibers) {
    const el = fiberToDOM(fiber);
    if (el) {
      rects.push(el.getBoundingClientRect());
    }
  }
  return rects;
}

/**
 * Compute the smallest bounding rect that contains all given rects.
 * Returns null when the input array is empty.
 */
export function getUnionRect(rects: DOMRect[]): DOMRect | null {
  if (rects.length === 0) return null;

  let minTop = Infinity;
  let minLeft = Infinity;
  let maxBottom = -Infinity;
  let maxRight = -Infinity;

  for (const r of rects) {
    if (r.top < minTop) minTop = r.top;
    if (r.left < minLeft) minLeft = r.left;
    if (r.bottom > maxBottom) maxBottom = r.bottom;
    if (r.right > maxRight) maxRight = r.right;
  }

  return new DOMRect(minLeft, minTop, maxRight - minLeft, maxBottom - minTop);
}

/* eslint-enable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment */
